import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
// Using CSS grid via Box for layout to avoid Grid type/version discrepancies
import {
  AccessTime as TimeIcon,
  Restaurant as RestaurantIcon,
  Storefront as InStoreIcon,
  ShoppingCart as OnlineIcon,
  PlayArrow as StartIcon,
  Done as ReadyIcon,
  CheckCircle as CompleteIcon,
  Add as AddIcon
} from '@mui/icons-material';

type OrderSource = 'online' | 'instore';
type OrderStatus = 'new' | 'in_progress' | 'ready';

interface OrderItem {
  name: string;
  quantity: number;
}

interface KitchenOrder {
  id: string;
  source: OrderSource;
  status: OrderStatus;
  items: OrderItem[];
  notes?: string;
  placedAt: Date;
  customerLabel?: string;
}

const sampleMenu: string[] = [
  'Birria Rice Bowl',
  'Chicken Tacos',
  'Steak Burrito',
  'Bean Rice Bowl',
  'Taco Salad',
  'Quesadilla',
  'Chips & Guac',
  'Street Corn'
];

function generateMockOrder(source: OrderSource): KitchenOrder {
  const itemCount = Math.floor(Math.random() * 3) + 1;
  const items: OrderItem[] = Array.from({ length: itemCount }).map(() => ({
    name: sampleMenu[Math.floor(Math.random() * sampleMenu.length)],
    quantity: Math.floor(Math.random() * 3) + 1
  }));
  const id = `${source === 'online' ? 'ON' : 'IS'}-${Math.floor(1000 + Math.random() * 9000)}`;
  return {
    id,
    source,
    status: 'new',
    items,
    notes: Math.random() > 0.7 ? 'No onions. Extra salsa.' : undefined,
    placedAt: new Date(),
    customerLabel: source === 'online' ? 'Pickup' : `Table ${Math.floor(1 + Math.random() * 20)}`
  };
}

function getElapsedSeconds(from: Date): number {
  return Math.floor((Date.now() - from.getTime()) / 1000);
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
}

const statusToTitle: Record<OrderStatus, string> = {
  new: 'New',
  in_progress: 'In Progress',
  ready: 'Ready'
};

const KitchenDisplay: React.FC = () => {
  const [orders, setOrders] = useState<KitchenOrder[]>([
    generateMockOrder('online'),
    generateMockOrder('instore'),
    generateMockOrder('online')
  ]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const ordersByStatus = useMemo(() => ({
    new: orders.filter(o => o.status === 'new'),
    in_progress: orders.filter(o => o.status === 'in_progress'),
    ready: orders.filter(o => o.status === 'ready')
  }), [orders, tick]);

  const moveToStatus = (id: string, next: OrderStatus) => {
    setOrders(prev => prev.map(o => (o.id === id ? { ...o, status: next } : o)));
  };

  const removeOrder = (id: string) => {
    setOrders(prev => prev.filter(o => o.id !== id));
  };

  const addOrder = (source: OrderSource) => {
    setOrders(prev => [generateMockOrder(source), ...prev]);
  };

  const renderOrderCard = (order: KitchenOrder) => {
    const elapsed = getElapsedSeconds(order.placedAt);
    const timeSeverity = elapsed < 5 * 60 ? 'default' : elapsed < 10 * 60 ? 'warning' : 'error';
    const SourceIcon = order.source === 'online' ? OnlineIcon : InStoreIcon;

    return (
      <Paper key={order.id} sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RestaurantIcon color="action" />
            <Typography variant="subtitle1" fontWeight="bold">{order.id}</Typography>
            <Chip
              size="small"
              icon={<SourceIcon />}
              label={order.source === 'online' ? 'Online' : 'In-Store'}
              color={order.source === 'online' ? 'primary' : 'secondary'}
              variant="outlined"
            />
            {order.customerLabel && (
              <Chip size="small" label={order.customerLabel} variant="outlined" />
            )}
          </Stack>
          <Chip
            size="small"
            icon={<TimeIcon />}
            label={formatDuration(elapsed)}
            color={timeSeverity as any}
            variant="outlined"
          />
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <List dense sx={{ pt: 0 }}>
          {order.items.map((item, idx) => (
            <ListItem key={idx} sx={{ pl: 0 }}>
              <ListItemText
                primary={`${item.quantity} × ${item.name}`}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>

        {order.notes && (
          <Typography variant="caption" color="text.secondary">Notes: {order.notes}</Typography>
        )}

        <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
          {order.status === 'new' && (
            <Button
              variant="contained"
              size="small"
              startIcon={<StartIcon />}
              onClick={() => moveToStatus(order.id, 'in_progress')}
            >
              Start
            </Button>
          )}
          {order.status === 'in_progress' && (
            <Button
              variant="contained"
              size="small"
              startIcon={<ReadyIcon />}
              onClick={() => moveToStatus(order.id, 'ready')}
            >
              Mark Ready
            </Button>
          )}
          {order.status === 'ready' && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CompleteIcon />}
              onClick={() => removeOrder(order.id)}
            >
              Complete
            </Button>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">Kitchen Display</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Add online order">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => addOrder('online')}>
              Online
            </Button>
          </Tooltip>
          <Tooltip title="Add in-store order">
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => addOrder('instore')}>
              In-Store
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {(['new', 'in_progress', 'ready'] as OrderStatus[]).map(status => (
          <Box key={status}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">{statusToTitle[status]}</Typography>
                <Chip size="small" label={ordersByStatus[status].length} />
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Box>
                {ordersByStatus[status].map(renderOrderCard)}
                {ordersByStatus[status].length === 0 && (
                  <Typography variant="body2" color="text.secondary">No orders</Typography>
                )}
              </Box>
            </Paper>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default KitchenDisplay;


